import React, { useRef, useEffect } from 'react'
import { mapEntries, VOLUMES } from '../utility'
import useInterval from '@use-it/interval'

import type { EffectId } from '@mission.io/mission-player'
import { useSilentMissionPhaseRef } from './hooks'
import {
	isLogin,
	getCurrentAmbianceId,
	useOnSpecialEffectEnd,
	useMissionStateSelector,
	useOnAmbianceEffectEnd,
	getSpecialEffects,
	useResolvers,
	useLocalStateSelector,
	useLocalDispatch,
	getLocalSoundEffects,
	endLocalSoundEffect,
	useControlledSoundEffect,
	useVolumeOf,
} from '../context'
import {
	type AmbianceSpecialEffectData,
	getSpecialEffect,
	getAmbianceEffect,
	type SpecialEffect,
	type SpecialEffectId,
} from '@mission.io/mission-toolkit'
import { AMBIANCE_TYPE_EFFECT } from '@mission.io/mission-toolkit/constants'

import MusicPlayer from './Music'
import { ConnectedAudio } from './ConnectedMedia'
import { updateLocalSoundEffectFade } from '../context/reducer'

/**
 * Runs audio for effects in the player - includes ambiance sound, special effect sound, and music (both background song and current song)
 * DOES NOT INCLUDE audio for videos, vocal tracks, and culminating moment start/finish
 */
export default function SoundEffects(): React$Node {
	const localDispatch = useLocalDispatch()
	const effects = useMissionStateSelector(getSpecialEffects)
	const localSoundEffects = useLocalStateSelector(getLocalSoundEffects)
	const currentAmbianceId = useMissionStateSelector(getCurrentAmbianceId)

	useLocalSoundEffectFadeOut(localSoundEffects, localDispatch)
	const onSpecialEffectEnd = useOnSpecialEffectEnd()
	const isLoginPhase = useMissionStateSelector(isLogin)
	const effectsVolume = useVolumeOf(VOLUMES.EFFECTS)

	const ambianceEffect =
		currentAmbianceId && getAmbianceEffect(currentAmbianceId)

	if (isLoginPhase) {
		return null
	}

	return (
		<div>
			{effects &&
				mapEntries(effects, (effect: SpecialEffect) => (
					<RunningAudio
						playWithMissionPhase
						key={effect._actionId}
						effectId={effect.id}
						persistInMissionPause={effect.persistInMissionPause}
						volume={effectsVolume}
						onEnded={() => {
							onSpecialEffectEnd(effect._actionId)
						}}
					/>
				))}
			{ambianceEffect && ambianceEffect.type === AMBIANCE_TYPE_EFFECT && (
				<AmbianceAudio volume={effectsVolume} ambianceEffect={ambianceEffect} />
			)}
			{localSoundEffects.map(soundEffect => {
				return (
					<RunningAudio
						key={soundEffect.id}
						effectId={soundEffect.id}
						volume={
							effectsVolume *
							(soundEffect.fadeVolumePercentage == null
								? 1
								: soundEffect.fadeVolumePercentage)
						}
						onEnded={() => {
							localDispatch(endLocalSoundEffect(soundEffect.id))
						}}
					/>
				)
			})}
			<MusicPlayer />
		</div>
	)
}

const FADE_INTERVAL = 50
const FADE_DELAY = 1000

/**
 * A hook to fade out local sound effects to avoid abrupt sound stops
 *
 * @param {$PropertyType<PlayerState, 'localSoundEffects'>} localSoundEffects the localSound effects from the player state
 * @param {Action => void} localDispatch the local action dispatch function used for updating the volume of the local sound effects
 */
function useLocalSoundEffectFadeOut(localSoundEffects, localDispatch) {
	const lastIntervalRunTime: { current: ?number } = useRef(null)
	let intervalDelay = FADE_INTERVAL
	const fadingSoundEffects: Array<{
		id: SpecialEffectId,
		fadeVolumePercentage: number,
	}> = (localSoundEffects.filter(
		soundEffect => soundEffect.fadeVolumePercentage != null
	): any) // we can cast this to any because we know that the filter will remove all null values
	const intervalIsDone = fadingSoundEffects.length === 0
	if (intervalIsDone) {
		// By setting the interval delay to null, the interval will be cleared
		intervalDelay = null
	}
	useEffect(() => {
		lastIntervalRunTime.current = null
	}, [intervalIsDone])

	useInterval(() => {
		const lastRunTime = lastIntervalRunTime.current
		const timeElapsedSinceLastInterval = lastRunTime
			? Date.now() - lastRunTime
			: FADE_INTERVAL
		const fadeIntervalPercentage = timeElapsedSinceLastInterval / FADE_DELAY
		fadingSoundEffects.forEach(soundEffect => {
			localDispatch(
				updateLocalSoundEffectFade(
					soundEffect.id,
					soundEffect.fadeVolumePercentage - fadeIntervalPercentage
				)
			)
		})
		lastIntervalRunTime.current = Date.now()
	}, intervalDelay)
}

function RunningAudio({
	effectId,
	volume,
	onEnded,
	playWithMissionPhase,
	persistInMissionPause,
}: {
	effectId: EffectId,
	volume: number,
	onEnded: () => void,
	playWithMissionPhase?: boolean,
	persistInMissionPause?: boolean,
}) {
	const controlledEffect = useControlledSoundEffect(effectId)
	const ref = useSilentMissionPhaseRef()

	const { soundEffectSourceResolver } = useResolvers()
	const wholeEffect = getSpecialEffect(effectId)
	if (
		!wholeEffect ||
		!wholeEffect.audioSrc ||
		controlledEffect?.isPlaying === false
	) {
		return null
	}

	return (
		<ConnectedAudio
			ref={playWithMissionPhase ? ref : null}
			id={`effect-${effectId}`}
			autoPlay
			onEnded={() => {
				if (!wholeEffect.loop) {
					onEnded()
				}
			}}
			src={soundEffectSourceResolver(wholeEffect.audioSrc)}
			volume={volume}
			loop={wholeEffect.loop}
			applyMissionPause={!persistInMissionPause}
		/>
	)
}

function AmbianceAudio({
	ambianceEffect,
	volume,
}: {
	ambianceEffect: AmbianceSpecialEffectData,
	volume: number,
}) {
	const controlledEffect = useControlledSoundEffect(ambianceEffect.id)
	const ambianceAudioRef = useSilentMissionPhaseRef()
	const onAmbianceEffectEnd = useOnAmbianceEffectEnd()
	const { soundEffectSourceResolver } = useResolvers()
	if (controlledEffect?.isPlaying === false || !ambianceEffect.audioSrc) {
		return null
	}
	return (
		<ConnectedAudio
			id={`effect-${ambianceEffect.id}`}
			autoPlay
			onEnded={() => {
				onAmbianceEffectEnd(ambianceEffect, () => {
					const currentAmbianceAudioRef = ambianceAudioRef.current
					if (!currentAmbianceAudioRef) {
						return
					}
					currentAmbianceAudioRef.play().catch(err => {
						throw new Error(
							`Failed to play ambianceAudioRef (src: ${currentAmbianceAudioRef.currentSrc}): ${err.message}`
						)
					})
				})
			}}
			src={soundEffectSourceResolver(ambianceEffect.audioSrc)}
			volume={volume}
			ref={ambianceAudioRef}
		/>
	)
}
